import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { ArtifactTypes } from '@mono/proto-clients';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { ErrorBoundary } from '../../../../components/ErrorBoundary';
import { useAttachmentPreview } from '../../../../hooks/useAttachmentPreview';
import { artifactClient } from '../../../../lib/connect/clients';
const ACCESS_DENIED_COPY = "You don't have access to this document.";
function isAccessDenied(error) {
    const code = ConnectError.from(error ?? new Error('')).code;
    return code === Code.PermissionDenied || code === Code.NotFound;
}
const IMAGE_EXTENSIONS = [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'webp',
    'svg',
    'bmp'
];
function isPreviewableMimeType(mimeType) {
    return mimeType === 'application/pdf' || mimeType.startsWith('image/');
}
// Some artifacts land with an empty or generic mime type (upload path did not
// sniff it), so fall back to the filename extension rather than showing
// "not available" for a file the browser could actually render.
function isPreviewableByExtension(filename) {
    const ext = filename.split('.').pop()?.toLowerCase() ?? '';
    return ext === 'pdf' || IMAGE_EXTENSIONS.includes(ext);
}
function isPdfByExtension(filename) {
    return filename.split('.').pop()?.toLowerCase() === 'pdf';
}
export function DocumentPreviewDialog({ artifactId, title, open, onOpenChange }) {
    const { data: artifactData, isError: artifactFailed, error: artifactError, isLoading: artifactLoading } = useQuery({
        queryKey: [
            'program-document-artifact',
            artifactId
        ],
        queryFn: ()=>artifactClient.getArtifact({
                artifactId
            }),
        enabled: open && !!artifactId,
        retry: false
    });
    const filename = artifactData?.artifact?.filename ?? '';
    const mimeType = artifactData?.artifact?.mimeType ?? '';
    const artifactStatus = artifactData?.artifact?.status;
    const failedReason = artifactData?.artifact?.classificationLabel ?? '';
    const accessDenied = artifactFailed && isAccessDenied(artifactError);
    const isPreviewable = useMemo(()=>{
        if (mimeType && mimeType !== 'application/octet-stream') {
            return isPreviewableMimeType(mimeType);
        }
        return isPreviewableByExtension(filename);
    }, [
        mimeType,
        filename
    ]);
    const isPdf = useMemo(()=>{
        if (mimeType && mimeType !== 'application/octet-stream') {
            return mimeType === 'application/pdf';
        }
        return isPdfByExtension(filename);
    }, [
        mimeType,
        filename
    ]);
    const { data: previewUrl, isLoading, error } = useAttachmentPreview({
        attachmentId: artifactId,
        enabled: open && !!artifactId && isPreviewable && !accessDenied
    });
    const previewAccessDenied = accessDenied || isAccessDenied(error);
    // A refused upload has no stored bytes: offering Download would be a dead button.
    const uploadFailed = artifactStatus === ArtifactTypes.ArtifactStatus.FAILED;
    const handleDownload = useCallback(async ()=>{
        // Open the popup synchronously inside the click gesture, then redirect it
        // once the presigned URL resolves. Awaiting first lets the browser strip
        // the user-gesture context and most popup blockers refuse the window.
        const win = window.open('', '_blank');
        try {
            const dl = await artifactClient.generateDownloadUrl({
                artifactId
            });
            if (dl.url && win) {
                win.location.href = dl.url;
            } else {
                win?.close();
            }
        } catch (err) {
            console.error('Failed to generate download URL', err);
            win?.close();
        }
    }, [
        artifactId
    ]);
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "max-w-4xl max-h-[90vh] overflow-hidden flex flex-col",
            "data-testid": Testids.deliveryDocs.documentPreview,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsxs(DialogTitle, {
                        className: "flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate pr-4",
                                children: title
                            }),
                            !previewAccessDenied && !uploadFailed && /*#__PURE__*/ _jsxs("button", {
                                onClick: handleDownload,
                                "aria-label": `Download ${title}`,
                                className: "flex items-center gap-2 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                "data-testid": Testids.deliveryDocs.documentPreviewDownload,
                                children: [
                                    /*#__PURE__*/ _jsx(Download, {
                                        className: "w-4 h-4"
                                    }),
                                    "Download"
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(ErrorBoundary, {
                    fallback: /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col items-center justify-center h-64 gap-4",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive",
                                children: "Failed to render preview"
                            }),
                            /*#__PURE__*/ _jsxs("button", {
                                onClick: handleDownload,
                                className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                children: [
                                    /*#__PURE__*/ _jsx(Download, {
                                        className: "w-4 h-4"
                                    }),
                                    "Download instead"
                                ]
                            })
                        ]
                    }),
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "flex-1 overflow-auto",
                        children: artifactLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center h-64",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Loading..."
                            })
                        }) : previewAccessDenied ? /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col items-center justify-center h-64 gap-4",
                            "data-testid": Testids.deliveryDocs.documentPreviewUnavailable,
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive",
                                children: ACCESS_DENIED_COPY
                            })
                        }) : uploadFailed && failedReason ? /*#__PURE__*/ _jsx("div", {
                            className: "flex flex-col items-center justify-center h-64 gap-4",
                            "data-testid": Testids.deliveryDocs.documentPreviewUnavailable,
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-destructive",
                                children: failedReason
                            })
                        }) : !isPreviewable ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center justify-center h-64 gap-4",
                            "data-testid": Testids.deliveryDocs.documentPreviewUnavailable,
                            children: [
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-muted-foreground",
                                    children: "Preview not available for this file type"
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleDownload,
                                    className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                    children: [
                                        /*#__PURE__*/ _jsx(Download, {
                                            className: "w-4 h-4"
                                        }),
                                        "Download to view"
                                    ]
                                })
                            ]
                        }) : isLoading ? /*#__PURE__*/ _jsx("div", {
                            className: "flex items-center justify-center h-64",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-muted-foreground",
                                children: "Loading..."
                            })
                        }) : error ? /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-col items-center justify-center h-64 gap-4",
                            children: [
                                /*#__PURE__*/ _jsxs("p", {
                                    className: "text-destructive",
                                    children: [
                                        "Error: ",
                                        error instanceof Error ? error.message : 'Failed to load preview'
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("button", {
                                    onClick: handleDownload,
                                    className: "flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90",
                                    children: [
                                        /*#__PURE__*/ _jsx(Download, {
                                            className: "w-4 h-4"
                                        }),
                                        "Download instead"
                                    ]
                                })
                            ]
                        }) : previewUrl ? /*#__PURE__*/ _jsx("div", {
                            className: "w-full h-full",
                            children: isPdf ? /*#__PURE__*/ _jsx("iframe", {
                                src: previewUrl,
                                className: "w-full h-[70vh]",
                                title: title,
                                "data-testid": Testids.deliveryDocs.documentPreviewFrame
                            }) : /*#__PURE__*/ _jsx("img", {
                                src: previewUrl,
                                alt: title,
                                className: "w-full h-auto object-contain",
                                "data-testid": Testids.deliveryDocs.documentPreviewFrame
                            })
                        }) : null
                    })
                })
            ]
        })
    });
}
